import { t } from "@lingui/macro";
import { parseJsonBlock } from "@smp/utils";
import { useSnackbar } from "notistack";
import { useCallback } from "react";

import { GPT_LANGUAGES } from "@/fixtures";
import { EPromptKey, usePromptFetcher } from "@/hooks/use_prompt_fetcher";
import { useMutationGptAskAsk } from "@/queries";

export const useGptAsk = () => {
  const getPrompt = usePromptFetcher();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: gptAsk } = useMutationGptAskAsk();

  return useCallback(
    async <Resp extends unknown>(
      key: EPromptKey,
      data?: Record<string, any>,
      params?: Omit<Parameters<typeof gptAsk>[0], "prompt" | "tag">,
    ): Promise<Resp> => {
      if (data?.language) {
        data.language =
          GPT_LANGUAGES.find(
            ({ id }) => id === data.language,
          )?.getDisplayName() || data.language;
      }

      const prompt = await getPrompt(key, data);

      const resp = await gptAsk({
        prompt,
        tag: "advisor",
        ...params,
      });

      if (!resp) {
        throw new Error("Wrong response");
      }

      const response = parseJsonBlock<Resp>(resp);

      if (!response) {
        enqueueSnackbar(
          t`Something went wrong... Try your request again later`,
          {
            variant: "error",
          },
        );

        throw new Error("Parse failing");
      }

      return response;
    },
    [enqueueSnackbar, getPrompt, gptAsk],
  );
};
